import { useMutation, useQuery } from "@tanstack/react-query";
import { getOrderTrackingEnquiry, exportOrderTrackingAllCustomer } from "services/data.report.service";
import { TableCard } from "ui-components/Layout";
import { ListLayout } from "ui-components/Layout";
import BasicTable from "ui-components/tables/BasicTable";
import { OrderTrackingColumns } from "./columns";
import { useState, useEffect } from "react";
import { useCustomerValue } from "providers/CustomerContext";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import { CSVLink } from "react-csv";
import { Texts } from "ui-components/Form";
import { Grid } from "@mui/material";
import { CustomDateRangePicker } from "ui-components/CustomInput";
import dayjs from "dayjs";
import { Error_ } from "ui-components/Alert";
import { LoadingDialog } from "ui-components/Alert";
import { useTeamValue } from "providers/TeamContext";
import { ExportDialog } from "ui-components/Alert";
import useCsvDownloadHook from "hooks/useCsvDownloadHook";
import useFileDownloadHook from "hooks/useFileDownloadHook";
import { getCustomerOptions } from "services/data.internal.service";
import { SelectFields } from "ui-components/Form";

function OrderTrackingEnquiry() {
  const { customerCode, customerOpt } = useCustomerValue();
  const [alert, setAlert] = useState(null);
  const [data, setData] = useState([]);
  const date = dayjs(new Date()).format("YYYY-MM-DD");
  const [startDate, setStartDate] = useState(date);
  const [endDate, setEndDate] = useState(date);
  const [filteredData, setFilteredData] = useState([]);
  const { downloadCsv, isDownloading } = useCsvDownloadHook();
  const { downloadFile, isDownloading: isExporting, error: exportError } = useFileDownloadHook();

  useEffect(() => {
    if (exportError) {
      setAlert(<Error_ content={exportError.message} onChange={setAlert} />);
    }
  }, [exportError]);
  const [customers, setCustomers] = useState([]);
  const [customerOptions, setCustomerOptions] = useState([]);
  const isAllCustomer = customers.includes("ALL CUSTOMER");
  const handleFilterRowsChange = (filteredData) => {
    setFilteredData(filteredData);
  };
  const { refetch, isFetching } = useQuery({
    queryKey: ["order-tracking-enquiry", { customers }],
    queryFn: () => getOrderTrackingEnquiry({ customers, customerCode, startDate, endDate }),
    enabled: Boolean(customerCode) && !isAllCustomer,
    onSuccess: (data) => {
      setData(data.data.data);
      setFilteredData(data.data.data);
    },
    onError: () => {},
  });
  const { permissions, allowAll } = useTeamValue();
  const exportPermission =
    allowAll ||
    (Boolean(permissions["Order Tracking_Report"]) &&
      permissions["Order Tracking_Report"].findIndex((b) => b === "export") !== -1);

  const customProps = { startDate, setStartDate, endDate, setEndDate };

  const validateDateRange = () => {
    setAlert(null);
    const valid =
      dayjs(endDate).isSame(dayjs(startDate)) || dayjs(endDate).isAfter(dayjs(startDate));
    if (!valid) {
      setAlert(<Error_ content="Start date cannot greater than end date" onChange={setAlert} />);
      return false;
    }
    if (dayjs(date).isBefore(endDate)) {
      setAlert(<Error_ content="End date cannot greater than today date" onChange={setAlert} />);
      return false;
    }
    return true;
  };

  const onFetch = () => {
    if (!validateDateRange()) return;
    refetch();
  };

  const onExportAllCustomer = () => {
    if (!validateDateRange()) return;
    const filename = `ALLCUSTOMER_${dayjs(new Date()).format("YYYYMMDD-HHmm")}_OrderTrackingNumber.xlsx`;
    downloadFile(() => exportOrderTrackingAllCustomer({ startDate, endDate }), filename);
  };

  useQuery({
    queryKey: ["customer-options", { customerCode }],
    queryFn: () => getCustomerOptions(),
    onSuccess: (data) => {
      const activeCust = customerOpt.filter((x) => data.data.some((y) => y === x));
      setCustomerOptions(activeCust);
      setCustomers([customerCode]);
    },
    onError: () => {},
  });

  return (
    <ListLayout>
      {isFetching && <LoadingDialog />}
      {(isDownloading || isExporting) && <ExportDialog />}
      {alert && <MDBox sx={{ my: 3 }}>{alert}</MDBox>}

      <TableCard sx={{ my: 3 }}>
        <MDBox margin="1rem 0 0 1rem">
          <Grid container spacing={3}>
            <Grid item xs={5}>
              {Boolean(customerOptions) && Boolean(customerOptions.length) && (
                <SelectFields
                  field={{
                    name: "",
                    label: "Customers",
                    value: customers,
                    errors: {},
                    multiple: true,
                  }}
                  options={["ALL CUSTOMER", ...customerOptions]}
                  onChange={(e, v) => {
                    if (v.includes("ALL CUSTOMER")) {
                      setCustomers(["ALL CUSTOMER"]);
                      // No table data exists for ALL CUSTOMER - it's an export-only file download.
                      setData([]);
                      setFilteredData([]);
                    } else {
                      setCustomers(v);
                    }
                  }}
                />
              )}
            </Grid>
          </Grid>
          <Grid container spacing={3}>
            <Grid item xs={4}>
              <CustomDateRangePicker {...customProps} />
            </Grid>
            <Grid item xs={4}>
              {isAllCustomer ? (
                <MDButton
                  variant="gradient"
                  color="info"
                  disabled={!exportPermission}
                  onClick={() => {
                    onExportAllCustomer();
                  }}
                >
                  export
                </MDButton>
              ) : (
                <>
                  <MDButton
                    variant="gradient"
                    color="info"
                    onClick={() => {
                      onFetch();
                    }}
                  >
                    search
                  </MDButton>
                  <MDButton
                    sx={{ marginLeft: "1rem" }}
                    variant="gradient"
                    color="info"
                    disabled={!exportPermission}
                    onClick={() => {
                      const filename = `${customerCode}_${dayjs(new Date()).format(
                        "YYYYMMDD-HHmm"
                      )}_OrderTrackingNumber`;
                      downloadCsv(filename, filteredData);
                    }}
                  >
                    export
                  </MDButton>
                </>
              )}
            </Grid>
          </Grid>
        </MDBox>

        {!isAllCustomer && (
          <BasicTable
            data={data}
            columns={OrderTrackingColumns}
            onFilterRowsChange={handleFilterRowsChange}
          />
        )}
      </TableCard>
    </ListLayout>
  );
}

export default OrderTrackingEnquiry;
